import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    LayoutDashboard,
    Calendar,
    FileText,
    Clock,
    FileCheck,
    UserCheck,
    Briefcase,
    DollarSign,
    Send,
    ClipboardList,
    Settings,
    LogOut,
    ChevronRight,
    ChevronLeft,
    Loader,
    Workflow,
    X, Building, CalendarDays, Banknote, Plane, FileCheck2, ReceiptText, UserRoundMinus, HandCoins, Route, History,
} from "lucide-react";
import "./Sidebar.css";
import logoAveiro from "../../../images/logo2_aveiro_sidebar_cropped.png";
import { getSidebarMenusByRoleRequest } from "../../../redux/actions/sidebarMenuActions";
import { useLocation, useNavigate } from "react-router-dom";
import { logout } from "../../../redux/actions/authActions";
import { useTranslation } from "react-i18next";

const Sidebar = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const [activeItem, setActiveItem] = useState(null);
    const [openSections, setOpenSections] = useState({});
    const [isCollapsed, setIsCollapsed] = useState(() => {
        const saved = localStorage.getItem("sidebarCollapsed");
        return saved === "true";
    });
    const [isMobileOpen, setIsMobileOpen] = useState(false);

    // store
    const selectedRole = useSelector((state) => state.role?.selectedRole);
    const { menus = [], menusRoleId, loading, error } = useSelector(
        (state) => state.sidebarMenus || {}
    );
    const currentUser = useSelector((state) => state?.auth?.user);

    // Les menus viennent du backend. Une entrée est traduite lorsqu'une clé
    // correspondante existe dans les fichiers i18n, sinon son libellé d'origine
    // est conservé afin de ne jamais masquer un nouveau menu.
    const translateMenuLabel = (name) => t(name || "", { defaultValue: name || "" });

    // Icons mapping
    const iconMap = useMemo(
        () => ({
            "layout-dashboard": LayoutDashboard,
            calendar: Calendar,
            "file-text": FileText,
            clock: Clock,
            "file-check": FileCheck,
            "user-check": UserCheck,
            briefcase: Briefcase,
            "dollar-sign": DollarSign,
            send: Send,
            "clipboard-list": ClipboardList,
            settings: Settings,
            "log-out": LogOut,
            info: FileText,
            building: Briefcase,
            work: Workflow,
            workflows: Workflow,
            "conges-absences": CalendarDays,
            "conges-et-absences": CalendarDays,
            "jours-travail": Clock,
            "jours-de-travail": Clock,
            "ordre-mission": Send,
            "gestion-des-salaires": Banknote,
            "soldes-de-conges": CalendarDays,
            "ordres-de-mission": Plane,
            "arrete-mensuel": FileCheck2,
            "fiches-de-paie": ReceiptText,
            "notes-frais": ReceiptText,
            "notes-de-frais": ReceiptText,
            attestations: FileCheck,
            "avances-prets": HandCoins,
            "avances-et-prets": HandCoins,
            "avantages-sociaux": Briefcase,
            trombinoscope: UserCheck,
            organigramme: Workflow,
            "feuilles-temps": ClipboardList,
            "feuilles-de-temps": ClipboardList,
            missions: Briefcase,
            "documents-rh": FileText,
            "avance-prets": HandCoins,
            "natures-de-missions": Route,
            offboarding: UserRoundMinus,
            "mes-conges": Calendar,
            "mes-feuilles-temps": Clock,
            "mes-ordres-missions": Briefcase,
            "mes-fiches-paie": FileText,
            "mes-notes-frais": DollarSign,
            "mes-attestations": FileCheck,
            "mes-avances-prets": FileCheck,
            "mes-avantages-sociaux": Building,
            "historique-demandes": History,
            "demande-mission": Send,
            "agenda-equipe": CalendarDays,
        }),
        []
    );

    const getIcon = (iconName) => iconMap[iconName] || LayoutDashboard;

    // Fetch menus
    useEffect(() => {
        const roleId = selectedRole?.id;
        const companyId = currentUser?.companyId;
        if (roleId && companyId) {
            dispatch(getSidebarMenusByRoleRequest(companyId, roleId));
        }
    }, [dispatch, selectedRole?.id, currentUser?.companyId]);

    // Reset active item when role changes
    useEffect(() => {
        setActiveItem(null);
    }, [selectedRole?.id]);

    // Organize menus
    const { allMenus } = useMemo(() => {
        if (!menus || menus.length === 0) return { allMenus: [] };

        const visibleMenus = menus.filter((m) => m.displayStatus === "VISIBLE");

        const roots = visibleMenus
            .filter((m) => !m.parentMenuId)
            .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

        const childrenByParent = {};
        roots.forEach((root) => {
            const children = visibleMenus
                .filter((m) => m.parentMenuId === root.id)
                .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
            if (children.length > 0) {
                childrenByParent[root.id] = children;
            }
        });

        const allMenus = roots.map((root) => {
            const items = childrenByParent[root.id] || [];
            return {
                ...root,
                items,
                hasChildren: items.length > 0,
            };
        });

        return { allMenus };
    }, [menus]);

    // Auto-open sections
    useEffect(() => {
        if (!allMenus || allMenus.length === 0) return;
        setOpenSections((prev) => {
            const next = { ...prev };
            allMenus.forEach((menu) => {
                if (menu.hasChildren && typeof next[menu.id] === "undefined") {
                    next[menu.id] = true;
                }
            });
            return next;
        });
    }, [allMenus]);

    // Sync active item with path
    useEffect(() => {
        if (!menus?.length) return;
        const found = menus.find((m) => m.routePath === location.pathname);
        if (found?.id) setActiveItem(found.id);
    }, [location.pathname, menus]);

    // Redirect to menu order 1 on role change
    useEffect(() => {
        if (loading || !menus || menus.length === 0) return;

        // A module/role switch must wait for the response associated with the
        // newly selected role.  Without this guard, the old sidebar can win the
        // race and redirect the user to a page from the previous module.
        if (String(menusRoleId) !== String(selectedRole?.id)) return;

        if (sessionStorage.getItem("roleChanged") === "true") {
            sessionStorage.removeItem("roleChanged");
            const visibleMenus = menus.filter((m) => m.displayStatus === "VISIBLE");
            
            // Find the root menu (no parent) with displayOrder === 1
            const rootOrder1 = visibleMenus.find((m) => !m.parentMenuId && m.displayOrder === 1);
            
            let targetPath = null;
            if (rootOrder1) {
                if (rootOrder1.routePath && rootOrder1.routePath.trim()) {
                    targetPath = rootOrder1.routePath;
                } else {
                    // If it is a folder/section, find the first visible child menu item
                    const children = visibleMenus
                        .filter((m) => m.parentMenuId === rootOrder1.id)
                        .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
                    const firstChild = children.find((m) => m.routePath && m.routePath.trim());
                    if (firstChild) {
                        targetPath = firstChild.routePath;
                    }
                }
            }
            
            // Fallback: first sorted root menu
            if (!targetPath) {
                const roots = visibleMenus
                    .filter((m) => !m.parentMenuId)
                    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
                
                for (const root of roots) {
                    if (root.routePath && root.routePath.trim()) {
                        targetPath = root.routePath;
                        break;
                    }
                    const children = visibleMenus
                        .filter((m) => m.parentMenuId === root.id)
                        .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
                    const firstChild = children.find((m) => m.routePath && m.routePath.trim());
                    if (firstChild) {
                        targetPath = firstChild.routePath;
                        break;
                    }
                }
            }
            
            if (targetPath) {
                navigate(targetPath);
            }
        }
    }, [menus, menusRoleId, loading, navigate, selectedRole?.id]);

    // Save collapse state
    useEffect(() => {
        localStorage.setItem("sidebarCollapsed", isCollapsed);
    }, [isCollapsed]);

    // Ajoutez cet useEffect pour détecter le redimensionnement de la fenêtre
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth <= 1200) {
                // En mode mobile, fermer la sidebar et le menu mobile
                setIsCollapsed(true);
                setIsMobileOpen(false);
            } else {
                // En mode desktop, restaurer l'état sauvegardé
                const saved = localStorage.getItem("sidebarCollapsed");
                setIsCollapsed(saved === "true");
                setIsMobileOpen(false);
            }
        };

        // Appeler au montage pour définir l'état initial correct
        handleResize();

        // Écouter les changements de taille
        window.addEventListener('resize', handleResize);

        // Nettoyage
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // The header owns the mobile menu button; the drawer is available on every page.
    useEffect(() => {
        const toggleMobileMenu = () => {
            if (window.innerWidth <= 768) setIsMobileOpen((open) => !open);
        };
        const closeOnEscape = (event) => {
            if (event.key === 'Escape') setIsMobileOpen(false);
        };
        window.addEventListener('toggle-mobile-sidebar', toggleMobileMenu);
        window.addEventListener('keydown', closeOnEscape);
        return () => {
            window.removeEventListener('toggle-mobile-sidebar', toggleMobileMenu);
            window.removeEventListener('keydown', closeOnEscape);
        };
    }, []);

    useEffect(() => {
        document.body.classList.toggle('mobile-sidebar-is-open', isMobileOpen);
        return () => document.body.classList.remove('mobile-sidebar-is-open');
    }, [isMobileOpen]);

    // Handlers
    const handleItemClick = (menu) => {
        setActiveItem(menu.id);
        if (menu.routePath && menu.routePath.trim()) {
            navigate(menu.routePath);
        }
        // Close mobile menu on navigation
        if (window.innerWidth <= 1200) {
            setIsMobileOpen(false);
        }
    };

    const toggleSection = (sectionId) => {
        if (!isCollapsed || isMobileOpen) {
            setOpenSections((prev) => ({
                ...prev,
                [sectionId]: !prev[sectionId],
            }));
        }
    };

    const handleLogout = () => {
        dispatch(logout());
        navigate("/");
    };

    const toggleSidebar = () => {
        if (window.innerWidth <= 768) {
            setIsMobileOpen(false);
            return;
        }
        setIsCollapsed(!isCollapsed);
    };

    const toggleMobileSidebar = () => {
        setIsMobileOpen(!isMobileOpen);
    };

    // UI states
    if (loading && menus.length === 0) {
        return (
            <div className={`sidebar ${isCollapsed ? "collapsed" : ""}`}>
                <div className="sidebar-loading">
                    <Loader className="spinner" size={24} />
                    {!isCollapsed && <p>{t("Chargement...")}</p>}
                </div>
            </div>
        );
    }

    if (!selectedRole) {
        return (
            <div className={`sidebar ${isCollapsed ? "collapsed" : ""}`}>
                <div className="sidebar-loading">
                    <Settings size={24} />
                    {!isCollapsed && <p>{t("Sélectionnez un rôle")}</p>}
                </div>
            </div>
        );
    }

    if (error && (!menus || menus.length === 0)) {
        return (
            <div className={`sidebar ${isCollapsed ? "collapsed" : ""}`}>
                <div className="sidebar-error">
                    <X size={24} />
                    {!isCollapsed && (
                        <>
                            <p>{t("Erreur chargement")}</p>
                            <small>{String(error)}</small>
                        </>
                    )}
                </div>
            </div>
        );
    }

    if (allMenus.length === 0 && !loading) {
        return (
            <div className={`sidebar ${isCollapsed ? "collapsed" : ""}`}>
                <div className="sidebar-loading">
                    <FileText size={24} />
                    {!isCollapsed && <p>{t("Aucun menu disponible")}</p>}
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Mobile overlay */}
            {isMobileOpen && (
                <div className="mobile-overlay" onClick={toggleMobileSidebar} />
            )}

            <div
                className={`sidebar ${isCollapsed ? "collapsed" : ""} ${
                    isMobileOpen ? "mobile-open" : ""
                }`}
            >
                {/* Header */}
                <div className="sidebar-header">
                    <div className="sidebar-logo">
                        <div className="logo-wrapper" onClick={() => navigate('/cards')}>
                            <img
                                src={logoAveiro}
                                alt={t("Logo My HR Aveiro")}
                                className="logo-image logo-full"
                            />
                        </div>
                    </div>

                    {/* Bouton collapse - flèche gauche quand ouvert, droite quand fermé */}
                    <button
                        className="collapse-btn"
                        onClick={toggleSidebar}
                        aria-label={isMobileOpen && window.innerWidth <= 768 ? t("Fermer le menu") : (isCollapsed ? t("Ouvrir la sidebar") : t("Réduire la sidebar"))}
                    >
                        {isMobileOpen && window.innerWidth <= 768 ? (
                            <X size={20}/>
                        ) : isCollapsed ? (
                            <ChevronRight size={20}/>
                        ) : (
                            <ChevronLeft size={20}/>
                        )}
                    </button>
                </div>

                {/* Menus */}
                <div className="sidebar-menu">
                    {allMenus.map((menu) => {
                        // Menu without children
                        if (!menu.hasChildren) {
                            const Icon = getIcon(menu.icon);
                            return (
                                <div key={menu.id} className="menu-section">
                                    <div
                                        className={`menu-item ${
                                            activeItem === menu.id ? "active" : ""
                                        }`}
                                        onClick={() => handleItemClick(menu)}
                                        title={translateMenuLabel(menu.name)}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        <Icon className="menu-icon" size={20} />
                                        <span className="menu-label">{translateMenuLabel(menu.name)}</span>
                                    </div>
                                </div>
                            );
                        }

                        // Menu with children
                        return (
                            <div key={menu.id} className="menu-section">
                                <div
                                    className="section-header"
                                    onClick={() => toggleSection(menu.id)}
                                    role="button"
                                    tabIndex={0}
                                    title={translateMenuLabel(menu.name)}
                                >
                                    <span className="section-title">{translateMenuLabel(menu.name)}</span>
                                    {menu.items?.length > 0 && (
                                        <ChevronRight
                                            size={16}
                                            className={`chevron-icon ${
                                                openSections[menu.id] ? "open" : ""
                                            }`}
                                        />
                                    )}
                                </div>

                                {menu.items?.length > 0 && (
                                    <div
                                        className={`section-items ${
                                            openSections[menu.id] ? "open" : ""
                                        }`}
                                    >
                                        {menu.items.map((child) => {
                                            const Icon = getIcon(child.icon);
                                            return (
                                                <div
                                                    key={child.id}
                                                    className={`menu-item ${
                                                        activeItem === child.id ? "active" : ""
                                                    }`}
                                                    onClick={() => handleItemClick(child)}
                                                    title={translateMenuLabel(child.name)}
                                                    role="button"
                                                    tabIndex={0}
                                                >
                                                    <Icon className="menu-icon" size={20} />
                                                    <span className="menu-label">{translateMenuLabel(child.name)}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Bottom */}
                <div className="sidebar-bottom">
                    <div
                        className="menu-item"
                        onClick={handleLogout}
                        title={t("Se déconnecter")}
                        role="button"
                        tabIndex={0}
                    >
                        <LogOut className="menu-icon" size={20} />
                        <span className="menu-label">{t("Se déconnecter")}</span>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Sidebar;
